import { useMemo } from 'react';
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { Channel } from '@src/models/channel';
import { applicationApi } from '@src/apis/admin';
import type { ApplicationContactResponseModel, ApplicationBusinessType } from '@uniquegood/realworld-admin-interface';
import { formatPhoneNumberForDisplay } from '@src/utils/phone';

type ChannelContactQueryKey = readonly ['channel-contact', string];

export interface UseChannelContactOptions {
    /**
     * React Query 옵션을 덮어씁니다.
     * `queryKey`와 `queryFn`은 내부에서 관리하므로 제외했습니다.
     */
    queryOptions?: Omit<
        UseQueryOptions<Channel, Error, Channel, ChannelContactQueryKey>,
        'queryKey' | 'queryFn'
    >;
}

/**
 * 채널 고객 정보 조회 훅
 *
 * @description
 * - 채널 ID를 받아 고객 정보를 조회합니다.
 * - ApplicationApi.getApplicationContact를 사용하여 실제 API를 호출합니다.
 */
export const useChannelContact = (
    channelId: string,
    options: UseChannelContactOptions = {}
): UseQueryResult<Channel> => {
    const { queryOptions } = options;
    const { staleTime, ...restQueryOptions } = queryOptions ?? {};

    const queryKey = useMemo<ChannelContactQueryKey>(
        () => ['channel-contact', channelId] as const,
        [channelId]
    );

    return useQuery<Channel, Error, Channel, ChannelContactQueryKey>({
        queryKey,
        queryFn: async () => {
            const { data } = await applicationApi.getApplicationContact(
                channelId,
                undefined // x-rwd-api-version은 default value 사용
            );

            // API 응답 타입: ApplicationContactResponseModelApiSuccessResponseModel
            // data.data가 ApplicationContactResponseModel
            const response: ApplicationContactResponseModel = data.data;
            
            // ApplicationBusinessType을 소문자로 변환
            const businessTypeMap: Record<ApplicationBusinessType, string> = {
                'None': 'none',
                'General': 'company',
                'Agency': 'agency',
                'School': 'k12',
                'University': 'university',
                'Government': 'public',
                'Museum': 'library',
            };
            const businessType = response?.businessType 
                ? businessTypeMap[response.businessType] || response.businessType.toLowerCase()
                : undefined;
            
            return {
                channelId,
                channelName: '',
                customerName: response?.organizationName ?? undefined,
                businessType,
                contactName: response?.contactName ?? undefined,
                contactTitle: response?.jobTitle ?? undefined,
                contactEmail: response?.email ?? undefined,
                contactPhone: response?.phone ? formatPhoneNumberForDisplay(response.phone) : undefined,
                memo: response?.memo ?? undefined,
                status: 'active',
            };
        },
        enabled: !!channelId,
        staleTime: staleTime ?? 30 * 1000,
        refetchOnMount: 'always',
        ...restQueryOptions,
    });
};

